import { Box } from "@mui/material";
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAppConfig } from "rwo-react";
import { MinSessionDurationFilter } from "../../../components";
import { parseDimension } from "../../../domain/mappers/metricsMappers";
import type { Dimension } from "../../../domain/models/metrics";
import { useCompanySettings } from "../../../hooks";
import { useSessionStorageState } from "../../../hooks/useSessionStorageState";
import { ActivityLineChart } from "../InternalUsageTab/components/ActivityLineChart";
import { ActivitySummaryCards } from "../InternalUsageTab/components/ActivitySummaryCards";
import { ActivityTrendChart } from "../InternalUsageTab/components/ActivityTrendChart";
import { FeedbackRatingsCard } from "../InternalUsageTab/components/FeedbackRatingsCard";
import { FilterSelect } from "../InternalUsageTab/components/FilterSelect";
import { StickyToolbarWrapper } from "../InternalUsageTab/components/StickyToolbarWrapper";
import { TimeFilter } from "../InternalUsageTab/components/TimeFilter";
import {
  CUMULATIVE_CHARTS_FEATURE_FLAG,
  DEFAULT_DIMENSION,
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
  DIMENSION_KEY,
  EARLIEST_DATA_DATE,
  END_DATE_KEY,
  LICENSE_IDS_KEY,
  MIN_SESSION_DURATION_KEY,
  START_DATE_KEY,
} from "../InternalUsageTab/constants";
import { parseMinSessionDuration } from "../InternalUsageTab/utils";
import { LicenseChips } from "./components/LicenseChips";
import { useExternalActivityTrendData } from "./hooks/useExternalActivityTrendData";
import { useExternalChartData } from "./hooks/useExternalChartData";
import { useExternalFeedbackRatings } from "./hooks/useExternalFeedbackRatings";
import { useHistoricalLicenses } from "./hooks/useHistoricalLicenses";

const SELECT_WIDTH = 540;
const FEEDBACK_FEATURE_FLAG = "uam_showFeedback_external_usage";

export interface ExternalUsageTabProps {
  hasTabs?: boolean;
}

export const ExternalUsageTab: React.FC<ExternalUsageTabProps> = ({
  hasTabs = true,
}) => {
  const [dimension, setDimension] = useSessionStorageState<Dimension>(
    DIMENSION_KEY,
    DEFAULT_DIMENSION,
    parseDimension,
  );
  const [startDate, setStartDate] = useSessionStorageState<Date>(
    START_DATE_KEY,
    DEFAULT_START_DATE,
  );
  const [endDate, setEndDate] = useSessionStorageState<Date>(
    END_DATE_KEY,
    DEFAULT_END_DATE,
  );
  const [licenseIds, setLicenseIds] = useSessionStorageState<string[]>(
    LICENSE_IDS_KEY,
    [],
  );
  const [minSessionDuration, setMinSessionDuration] = useSessionStorageState<
    number | null
  >(MIN_SESSION_DURATION_KEY, null, parseMinSessionDuration);

  const { licenseOptions, licenseByIdMap } = useHistoricalLicenses();

  const licenseLabelById = useMemo(
    () => new Map(licenseOptions.map((o) => [o.value, o.label])),
    [licenseOptions],
  );

  const selectedLicenses = useMemo(
    () =>
      licenseIds.map((id) => ({
        id,
        label: licenseLabelById.get(id) ?? id,
        ...licenseByIdMap[id],
      })),
    [licenseIds, licenseLabelById, licenseByIdMap],
  );

  const filter = useMemo(
    () => ({
      startDate,
      endDate,
      dimension,
      licenseIds,
      minSessionDurationInMinutes: minSessionDuration ?? undefined,
    }),
    [startDate, endDate, dimension, licenseIds, minSessionDuration],
  );

  const {
    chartData,
    legendValues,
    isLoading: isActivityLoading,
    isFetching: isActivityFetching,
  } = useExternalChartData(filter);

  const { featureFlags } = useAppConfig();
  const showFeedback = useMemo(
    () => featureFlags[FEEDBACK_FEATURE_FLAG],
    [featureFlags],
  );
  const showCumulativeCharts = Boolean(
    featureFlags[CUMULATIVE_CHARTS_FEATURE_FLAG],
  );

  const {
    chartData: activityTrendData,
    isLoading: isActivityTrendLoading,
    isFetching: isActivityTrendFetching,
  } = useExternalActivityTrendData({
    ...filter,
    enabled: showCumulativeCharts,
  });

  const { companyId } = useParams();
  const { isExternalUserLevelDataConsentEnabled } = useCompanySettings({
    companyId,
  });

  const {
    feedbackData,
    overallRating,
    isLoading: isFeedbackLoading,
    isFetching: isFeedbackFetching,
  } = useExternalFeedbackRatings({
    selectedLicenses,
    startDate,
    endDate,
  });

  const selectedLicenseNames = useMemo(
    () => selectedLicenses.map((l) => l.label),
    [selectedLicenses],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        paddingBottom: 4,
      }}
    >
      <StickyToolbarWrapper hasTabs={hasTabs}>
        <Box
          sx={{
            alignItems: { xs: "stretch", md: "center" },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "space-between",
            height: { xs: "auto", md: 60 },
          }}
        >
          <TimeFilter
            dimension={dimension}
            earliestSelectableDate={EARLIEST_DATA_DATE}
            endDate={endDate}
            onDimensionChange={setDimension}
            setEndDate={setEndDate}
            setStartDate={setStartDate}
            startDate={startDate}
          />

          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 1,
            }}
          >
            <MinSessionDurationFilter
              onChange={setMinSessionDuration}
              value={minSessionDuration}
            />
            <FilterSelect
              desktopMenuMaxWidth={SELECT_WIDTH}
              emptySelectionMode="zero"
              label="License"
              onChange={setLicenseIds}
              options={licenseOptions}
              selectedValues={licenseIds}
            />
          </Box>
        </Box>
      </StickyToolbarWrapper>

      <ActivitySummaryCards
        aggregateValues={legendValues}
        isFetching={isActivityFetching}
        isLoading={isActivityLoading}
        isUserLevelDataEnabled={isExternalUserLevelDataConsentEnabled}
      />

      <ActivityLineChart
        data={chartData}
        endDate={endDate}
        isFetching={isActivityFetching}
        isLoading={isActivityLoading}
        isUserLevelDataEnabled={isExternalUserLevelDataConsentEnabled}
        selectedLicenseNames={selectedLicenseNames}
        startDate={startDate}
      />

      {showCumulativeCharts && (
        <ActivityTrendChart
          data={activityTrendData}
          endDate={endDate}
          isFetching={isActivityTrendFetching}
          isLoading={isActivityTrendLoading}
          isUserLevelDataEnabled={isExternalUserLevelDataConsentEnabled}
          selectedLicenseNames={selectedLicenseNames}
          startDate={startDate}
        />
      )}

      {showFeedback && (
        <FeedbackRatingsCard
          data={feedbackData}
          endDate={endDate}
          isFetching={isFeedbackFetching}
          isLoading={isFeedbackLoading}
          isUserLevelDataEnabled={isExternalUserLevelDataConsentEnabled}
          overallRating={overallRating}
          startDate={startDate}
        />
      )}

      <LicenseChips licenses={selectedLicenses} />
    </Box>
  );
};
